import {
  ServerBusinessRefundSettingsThresholdInsertInput,
  ServerBusinessRefundSettingsType,
  ServerSaleEntityTypeEnum,
} from '@expane/data'

const UNIT_IDS = {
  hours: 1,
  days: 2,
}

export const units = [
  { id: UNIT_IDS.hours, name: 'refundsFromClients.units.hours' },
  { id: UNIT_IDS.days, name: 'refundsFromClients.units.days' },
]

interface RefundInterval {
  hours: number
  percentage: number
}

interface RefundIntervalFromServer extends RefundInterval {
  id: number
}

export type RefundForm = {
  percentage: string
  unitId: number | undefined
  unitValue: string
}

export interface RefundsFromClientsSettingsForm {
  isRefundSubscriptionAllowed: boolean
  refundSubscription: RefundForm[]
  isRefundGiftCardAllowed: boolean
  refundGiftCard: RefundForm[]
  isRefundBookingAllowed: boolean
  refundBooking: RefundForm[]
}

const getHoursFromRefundForm = (input: RefundForm): number => {
  const unitValue = Number(input.unitValue)
  return input.unitId === UNIT_IDS.hours ? unitValue : unitValue * HOURS_IN_DAY
}

export const convertRefundFormToServer = (
  inputs: RefundForm[],
  policyId: number,
): ServerBusinessRefundSettingsThresholdInsertInput[] =>
  inputs.map(input => ({
    businessRefundSettingsPolicyId: policyId,
    hours: getHoursFromRefundForm(input),
    percentage: Number(input.percentage),
  }))

export const getRefundFormInputs = (businessRefundSettings: ServerBusinessRefundSettingsType) => {
  const refundBookingSettings = businessRefundSettings?.refundSettingsPolicies.find(
    policies => policies.type === ServerSaleEntityTypeEnum.Booking,
  )

  const refundBooking = sortRefundFormInputs(
    convertBusinessRefundSettingsToRefundForm(refundBookingSettings?.thresholds ?? []),
  )

  const refundGiftCardSettings = businessRefundSettings?.refundSettingsPolicies.find(
    policies => policies.type === ServerSaleEntityTypeEnum.GiftCard,
  )

  const refundGiftCard = sortRefundFormInputs(
    convertBusinessRefundSettingsToRefundForm(refundGiftCardSettings?.thresholds ?? []),
  )

  const refundSubscriptionSettings = businessRefundSettings?.refundSettingsPolicies.find(
    policies => policies.type === ServerSaleEntityTypeEnum.Subscription,
  )

  const refundSubscription = sortRefundFormInputs(
    convertBusinessRefundSettingsToRefundForm(refundSubscriptionSettings?.thresholds ?? []),
  )

  return {
    refundBooking,
    isRefundBookingAllowed: refundBookingSettings?.isRefundAllowed ?? false,
    refundGiftCard,
    isRefundGiftCardAllowed: refundGiftCardSettings?.isRefundAllowed ?? false,
    refundSubscription,
    isRefundSubscriptionAllowed: refundSubscriptionSettings?.isRefundAllowed ?? false,
  }
}

export const sortRefundFormInputs = (inputs: RefundForm[]): RefundForm[] => {
  return [...inputs].sort((a, b) => {
    const aHours = Number(a.unitValue) * (a.unitId === UNIT_IDS.days ? HOURS_IN_DAY : 1)
    const bHours = Number(b.unitValue) * (b.unitId === UNIT_IDS.days ? HOURS_IN_DAY : 1)
    return aHours - bHours
  })
}

const convertBusinessRefundSettingsToRefundForm = (
  thresholds: RefundIntervalFromServer[],
): RefundForm[] => {
  return thresholds.map(threshold => {
    const hours = threshold.hours

    const isFullDays = hours % HOURS_IN_DAY === 0

    const unitId = isFullDays ? UNIT_IDS.days : UNIT_IDS.hours
    const unitValue = isFullDays ? (hours / HOURS_IN_DAY).toString() : hours.toString()

    return {
      percentage: threshold.percentage.toString(),
      unitId,
      unitValue,
    }
  })
}

const HOURS_IN_DAY = 24
